/**
 * The theatre's wiring: the address to a subject, the subject to its neighbours, and the one read
 * the card makes that the catalogue did not already answer.
 *
 * Decides nothing. `model/theatreNeighbours` owns the walk, `model/exerciseLibrarySearch` owns the
 * address, `entities/exercise-library` owns what a movement IS and who owns it.
 *
 * ── The subject costs NO read ───────────────────────────────────────────────────────────────
 * `GET /exercise` returns the whole description of every movement — prose, steps, resolved links,
 * film — and `useExerciseLibrary` already holds it as `detailsById`. So opening a card is a lookup
 * in a map that was populated before the first row was painted: the hero, the rail and the bar are
 * complete on the theatre's first frame, and there is nothing in the panel that can arrive late and
 * push something. The ONE thing that does arrive late is the similar strip, which is why it has a
 * reserve and a gate of its own.
 *
 * ── The card is drawn from the CATALOGUE, not from the filtered list ────────────────────────
 * `?cwiczenie=<id>` names a movement, and a movement the trainer has since filtered out is still
 * the movement they opened. Closing the card under them because they typed in the search box would
 * be the screen taking something away that they did not put down. What DOES go is the walk: a
 * movement with no position in the list has no neighbours, and `neighboursOf` answers `null` rather
 * than guessing at an index.
 *
 * ── The row behind is kept in step, which is also what focus returns to ─────────────────────
 * Every open and every step points the table's own selection at the same movement, so the list the
 * trainer uncovers on Escape is the one the card left them on. `finalFocus` is a FUNCTION rather
 * than a ref for the same reason: it looks the row up by `data-exercise-row` at the moment the
 * dialog closes, which survives the subject having changed under the walk and the row having been
 * re-rendered — where a ref captured on the opening click would point at a detached node.
 *
 * ── The keyboard walk fires from the POPUP and from nothing inside it ───────────────────────
 * ←/→ and ↑/↓ step the card, and that is the canon's own control. But every one of those keys
 * already means something to what the card contains: a caret in the materials editor's address
 * field, a line in the rail's own scroller, the next item in the `…` menu. A handler on the popup
 * sees all of them, because a keydown bubbles.
 *
 * So the target is compared against the popup itself. `ui/ExerciseTheatre` points `initialFocus` at
 * that element — Base UI's own default is the first TABBABLE, which here is the walk's „poprzednie"
 * square — so the walk is live the moment the card appears and stands down the moment the trainer
 * Tabs into it. A predicate that named the things to yield TO instead ("not a field") was the
 * earlier shape, and it let ↓ swap the movement while the rail had 322px left to scroll and
 * ArrowRight discard a half-typed link.
 *
 * It is also why the popup element must OUTLIVE the subject: a step that remounted it ran Base UI's
 * focus return onto the row behind, and the press that arrived in that window was dropped. See
 * `ui/ExerciseTheatre`.
 */

import { useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  actionsFor,
  exerciseSimilarQueryOptions,
  ownershipOf,
  type ExerciseActions,
  type ExerciseDetail,
  type ExerciseOwnership,
} from '~/entities/exercise-library';
import { useSessionUser } from '~/entities/session';
import type { PointedRow } from '~/shared/lib/usePointedRow';

import {
  exerciseCardIdOf,
  exerciseCardSearch,
  validateExerciseLibrarySearch,
} from '../model/exerciseLibrarySearch';
import { toExerciseRow, type ExerciseRow } from '../model/exerciseRow';
import type { ExerciseScope } from '../model/exerciseScope';
import {
  neighbourAfterRemoval,
  neighboursOf,
  type TheatreNeighbours,
} from '../model/theatreNeighbours';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

/** How many of the endpoint's eight the canon draws. */
const SIMILAR_CARDS = 4;

/** The movement the card is about, in both the shapes its parts read. */
export interface TheatreSubject {
  readonly detail: ExerciseDetail;
  /** The same movement through the table's own projection: its still, its type word, its pills. */
  readonly row: ExerciseRow;
  readonly ownership: ExerciseOwnership;
  readonly actions: ExerciseActions;
}

/** The strip under the hero. `pending` and `error` are states, never an empty array. */
export interface TheatreSimilar {
  readonly status: 'pending' | 'error' | 'ready';
  readonly cards: readonly ExerciseRow[];
}

export interface TheatreState {
  /**
   * Whether the card is open — the dialog's own `open`, and the LIVE answer.
   *
   * False the moment the address stops naming a movement the catalogue holds, which is one render
   * before `subject` goes.
   */
  readonly isOpen: boolean;
  /**
   * The movement the card DRAWS. `null` until one has been opened, and it OUTLIVES `isOpen`: a
   * closing dialog is still a rendered dialog, and taking its popup away early strands Base UI's
   * backdrop over the page. See the hook.
   */
  readonly subject: TheatreSubject | null;
  readonly neighbours: TheatreNeighbours | null;
  readonly similar: TheatreSimilar;
  /** Open the card on a movement. Pushes, so the browser's Back closes it. */
  readonly open: (exerciseId: number) => void;
  /** Swap the subject without closing — the walk, and a press on a similar card. Replaces. */
  readonly goTo: (exerciseId: number) => void;
  readonly close: () => void;
  /** Close because the movement was deleted: focus lands on its neighbour's row, else the `h1`. */
  readonly closeAfterRemoval: () => void;
  /** ←/→ and ↑/↓ on the POPUP itself. Everything inside the card keeps its own arrow keys. */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  /** Where focus goes when the card closes: the row of whichever movement it ended on. */
  readonly finalFocus: () => HTMLElement | null;
}

export interface TheatreInput {
  /** The FILTERED, SORTED list — what the walk runs over. */
  readonly rows: readonly ExerciseRow[];
  /** The whole catalogue, which is where a subject is looked up. */
  readonly detailsById: ReadonlyMap<number, ExerciseDetail>;
  readonly scope: ExerciseScope;
  /** The table's selection, kept in step with the card. */
  readonly pointer: PointedRow;
}

export function useTheatre({ rows, detailsById, scope, pointer }: TheatreInput): TheatreState {
  const { t } = useTranslation();
  const contentLang = useContentLanguage();
  const navigate = useNavigate();
  const trainerId = useSessionUser()?.trainerId ?? null;

  // `strict: false` re-validated through the slice's own validator, exactly as `useExerciseLibrary`
  // reads the scope: one definition of what this address understands.
  const openId = exerciseCardIdOf(validateExerciseLibrarySearch(useSearch({ strict: false })));

  /**
   * Whether a card is open RIGHT NOW: the address names a movement and the catalogue holds it.
   *
   * Separate from the subject below, because the two do not end together. See `TheatreState.isOpen`.
   */
  const isOpen = openId !== null && detailsById.has(openId);

  /**
   * The movement the card DRAWS, which outlives the address that named it.
   *
   * `Dialog.Popup` must stay rendered through its own closing sequence. Base UI decides a closed
   * dialog is finished by waiting on the animations of the element `popupRef` points at
   * (`useOpenStateTransitions` → `useOpenChangeComplete` → `useAnimationsFinished`), and that wait
   * RETURNS WITHOUT CALLING BACK when the ref resolves to `null`. Taking the popup out of the tree
   * on the same commit that clears `?cwiczenie=` therefore left the store's `mounted` true for
   * ever: `Dialog.Backdrop` stayed on the page with `data-ending-style`, a full-viewport scrim that
   * swallowed every later press, and the shell stayed `inert`. It is the trap
   * `CreatePlanDialog` and `DayDock` avoid by rendering their popup unconditionally.
   *
   * So the last id the catalogue could answer is held, and the popup is drawn from it until Base UI
   * takes the whole portal away by itself. The guarded `setState` during render is React's own
   * "adjust state from a previous render" — the same shape `shared/lib/useSwapReplay` uses, and
   * the only one available: the React Compiler refuses a ref written in render.
   */
  const live = openId === null ? null : (detailsById.get(openId) ?? null);
  const [drawn, setDrawn] = useState<ExerciseDetail | null>(isOpen ? live : null);
  if (isOpen && live !== drawn) setDrawn(live);

  /* The DESCRIPTION is held, not only the id. Since the creator can delete the movement a card is
   * about, the catalogue that answers after the delete no longer holds it — and a subject looked up
   * by id would vanish on that refetch, taking the popup out mid-close: the stranded-backdrop defect
   * above, reached from the other side. While the card is open the held record follows every
   * refetch (a save, a new cover), because a changed record is a new object. */
  const detail = drawn;

  const similarRead = useQuery({
    ...exerciseSimilarQueryOptions(detail?.id ?? 0, contentLang),
    // `isOpen` as well as a subject: the held record outlives the movement itself after a delete,
    // and the catalogue's invalidation would otherwise refetch its `/similar` into a 404.
    enabled: detail !== null && isOpen,
  });

  /* Where focus goes when a card closes because its movement was DELETED. The row focus would
   * return to is gone, so the card names the neighbour it should land on instead — selected, which
   * pins it into the virtual range so it is mounted — or, with no neighbour, the screen's heading. */
  const [landing, setLanding] = useState<{ readonly id: number | null } | null>(null);

  // No `useMemo`: the React Compiler is on for this app (the opposite of the phone), and a
  // hand-written memo here would be exactly the reflex `CLAUDE.md` names. The projection is four
  // field reads over one record.
  const ownership = detail === null ? null : ownershipOf(detail, trainerId);
  const subject: TheatreSubject | null =
    detail === null || ownership === null
      ? null
      : {
          detail,
          row: toExerciseRow(detail, (key) => t(key)),
          ownership,
          actions: actionsFor(ownership),
        };

  /**
   * The strip's cards, taken back through the CATALOGUE rather than drawn from the similar
   * response.
   *
   * `SimilarExerciseResponse` carries only `coverImageUrl`, and the catalogue measured 385 posters
   * against 82 covers — so a strip drawn from the response alone would fall back to a tile on most
   * of its cards while the photograph of the same movement was already in the browser. Every id the
   * endpoint can return is in the catalogue by construction (it answers out of the shared library
   * plus the reader's own, which is what `GET /exercise` returns), and one that is not is dropped
   * rather than drawn half-described.
   */
  const similarCards: readonly ExerciseRow[] = (similarRead.data ?? []).flatMap((item) => {
    const found = item.id === undefined ? undefined : detailsById.get(item.id);
    return found === undefined ? [] : [toExerciseRow(found, (key) => t(key))];
  }).slice(0, SIMILAR_CARDS);

  // `point` off the pointer rather than the pointer itself: it is `usePointedRow`'s own `useState`
  // setter and never changes, where the hook's returned OBJECT is new on every selection. `open`
  // below closes over this function and is handed to all 479 table rows as a prop, so an identity
  // that moved with the selection would re-render every row on every pointer move — which is the
  // cost `ExerciseTableRow` exists to remove.
  const { point } = pointer;

  const goToId = (exerciseId: number, replace: boolean) => {
    point(String(exerciseId));
    void navigate({ to: '/cwiczenia', search: exerciseCardSearch(scope, exerciseId), replace });
  };

  return {
    isOpen,
    subject,
    neighbours: subject === null ? null : neighboursOf(rows, subject.detail.id),
    similar: {
      status: similarRead.isPending ? 'pending' : similarRead.isError ? 'error' : 'ready',
      cards: similarCards,
    },
    open: (exerciseId) => {
      setLanding(null);
      goToId(exerciseId, false);
    },
    goTo: (exerciseId) => {
      // REPLACES: a trainer who walked twenty movements must not have to press Back twenty times
      // to get out of the card. The address still names what is on screen, which is what a link
      // sent to a colleague needs.
      goToId(exerciseId, true);
    },
    close: () => {
      void navigate({ to: '/cwiczenia', search: exerciseCardSearch(scope, null) });
    },
    closeAfterRemoval: () => {
      const neighbour =
        subject === null ? null : (neighbourAfterRemoval(rows, subject.detail.id)?.id ?? null);
      setLanding({ id: neighbour });
      if (neighbour !== null) point(String(neighbour));
      void navigate({ to: '/cwiczenia', search: exerciseCardSearch(scope, null) });
    },
    onKeyDown: (event) => {
      // The popup and nothing in it. See the header: a field, a scroller and a menu all read the
      // same four keys, and all of them are descendants that bubble through here.
      if (event.target !== event.currentTarget) return;
      const neighbours = subject === null ? null : neighboursOf(rows, subject.detail.id);
      if (neighbours === null) return;
      const step =
        event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? neighbours.next
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
            ? neighbours.previous
            : null;
      if (step === null) return;
      event.preventDefault();
      goToId(step.id, true);
    },
    finalFocus: () => {
      if (landing !== null) {
        const neighbour =
          landing.id === null
            ? null
            : document.querySelector<HTMLElement>(`[data-exercise-row="${String(landing.id)}"]`);
        // The screen's `h1` carries `tabIndex={-1}` for exactly this.
        return neighbour ?? document.querySelector<HTMLElement>('main h1');
      }
      return subject === null
        ? null
        : document.querySelector<HTMLElement>(`[data-exercise-row="${String(subject.detail.id)}"]`);
    },
  };
}
